import { useEffect, useState } from "react";
import { Loader2, DollarSign, Receipt } from "lucide-react";
import { getInsuranceBilling } from "@/Calls/callInsuranceAuth";
import { Card } from "@/components/ui/card";

interface Charge {
    charges_id: number;
    charges_fee_description: string;
    charges_source_name: string;
    charges_amount: number;
    charges_created_at: string;
}

export default function InsuranceBillingPage() {
    const [data, setData] = useState<{ charges: Charge[]; total: number; billing_invoice_no: number | null } | null>(null);
    const [loading, setLoading] = useState(true);
    const [monthFilter, setMonthFilter] = useState("");

    useEffect(() => {
        getInsuranceBilling()
            .then(setData)
            .catch(console.error)
            .finally(() => setLoading(false));
    }, []);

    if (loading) return (
        <div className="flex items-center justify-center min-h-64">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
    );

    const charges = data?.charges ?? [];
    const filtered = monthFilter
        ? charges.filter((c) => c.charges_created_at?.startsWith(monthFilter))
        : charges;
    const filteredTotal = filtered.reduce((s, c) => s + c.charges_amount, 0);

    return (
        <div className="space-y-6 max-w-5xl mx-auto">
            <div>
                <h1 className="text-2xl font-light uppercase tracking-wide">Billing</h1>
                <p className="text-muted-foreground text-sm mt-1">
                    Itemized lead charges · Invoice #{data?.billing_invoice_no ?? "—"}
                </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
                <Card className="p-5 flex items-center gap-4">
                    <DollarSign className="h-10 w-10 text-amber-500" />
                    <div>
                        <p className="text-sm text-muted-foreground">Total Charges (All Time)</p>
                        <p className="text-2xl font-bold">${(data?.total ?? 0).toFixed(2)}</p>
                    </div>
                </Card>
                <Card className="p-5 flex items-center gap-4">
                    <Receipt className="h-10 w-10 text-blue-500" />
                    <div>
                        <p className="text-sm text-muted-foreground">Leads Pulled</p>
                        <p className="text-2xl font-bold">{charges.length}</p>
                    </div>
                </Card>
            </div>

            <div className="flex items-center gap-3">
                <label className="text-sm font-medium">Filter by Month</label>
                <input
                    type="month"
                    value={monthFilter}
                    onChange={(e) => setMonthFilter(e.target.value)}
                    className="rounded-lg border bg-background px-3 py-1.5 text-sm"
                />
                {monthFilter && (
                    <button onClick={() => setMonthFilter("")} className="text-sm text-muted-foreground hover:text-foreground">
                        Clear
                    </button>
                )}
            </div>

            {filtered.length === 0 ? (
                <Card className="p-12 text-center border-dashed">
                    <Receipt className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                    <p className="font-medium">No charges</p>
                </Card>
            ) : (
                <Card>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="border-b">
                                <tr className="text-left">
                                    <th className="p-4 font-medium">Date</th>
                                    <th className="p-4 font-medium">Customer</th>
                                    <th className="p-4 font-medium">Description</th>
                                    <th className="p-4 font-medium text-right">Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((c) => (
                                    <tr key={c.charges_id} className="border-b last:border-0 hover:bg-muted/30">
                                        <td className="p-4 text-muted-foreground">
                                            {new Date(c.charges_created_at).toLocaleDateString()}
                                        </td>
                                        <td className="p-4">{c.charges_source_name}</td>
                                        <td className="p-4">{c.charges_fee_description}</td>
                                        <td className="p-4 text-right font-medium">${c.charges_amount.toFixed(2)}</td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot className="border-t bg-muted/30">
                                <tr>
                                    <td colSpan={3} className="p-4 font-semibold">
                                        {monthFilter ? "Filtered Total" : "Total"}
                                    </td>
                                    <td className="p-4 text-right font-bold">${filteredTotal.toFixed(2)}</td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </Card>
            )}
        </div>
    );
}
